// frontend/src/modules/admin/components/ModeTestActions.jsx
import React, { useState } from 'react';
import { Zap, AlertCircle } from 'lucide-react';

/**
 * Panneau d'actions rapides pour forcer les transitions de statuts en mode TEST
 * À afficher dans GestionStatutsCommandes quand mode_test = 'oui'
 */
const ModeTestActions = ({ config, onActionComplete }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Ne rien afficher si mode_test n'est pas activé
  if (!config || config.mode_test !== 'oui') {
    return null;
  }

  const actions = [
    {
      id: 'attente_vers_confirme',
      label: 'Attente → Confirmé',
      description: 'Confirmer toutes les réservations en attente',
      statut_source: 'attente',
      statut_cible: 'confirme',
      color: 'blue'
    },
    {
      id: 'confirme_vers_recolte',
      label: 'Confirmé → En récolte',
      description: 'Passer les confirmées en récolte',
      statut_source: 'confirme',
      statut_cible: 'en_recolte',
      color: 'yellow'
    },
    {
      id: 'recolte_vers_pret',
      label: 'En récolte → Prêt',
      description: 'Marquer comme prêtes à retirer',
      statut_source: 'en_recolte',
      statut_cible: 'pret',
      color: 'green'
    },
    {
      id: 'pret_vers_retire',
      label: 'Prêt → Retiré',
      description: 'Marquer comme retirées',
      statut_source: 'pret',
      statut_cible: 'retire',
      color: 'purple'
    }
  ];

  const executeAction = async (action) => {
    if (loading) return;

    if (!window.confirm(`⚡ Forcer la transition : ${action.label}\n\n${action.description}\n\nContinuer ?`)) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/reservations/forcer-transition', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          statut_source: action.statut_source,
          statut_cible: action.statut_cible
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ ${data.modifiees || 0} réservation(s) mise(s) à jour vers "${action.statut_cible}"`);
        if (onActionComplete) {
          await onActionComplete();
        }
      } else {
        throw new Error(data.error || 'Erreur lors du forçage');
      }
    } catch (error) {
      console.error('Erreur forçage statut:', error);
      setMessage(`❌ Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const colorClasses = {
    blue: 'bg-blue-500 hover:bg-blue-600',
    yellow: 'bg-yellow-500 hover:bg-yellow-600',
    green: 'bg-green-500 hover:bg-green-600',
    purple: 'bg-purple-500 hover:bg-purple-600'
  };

  return (
    <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-6">
      <div className="flex items-start gap-4">
        <Zap className="h-8 w-8 text-yellow-600 flex-shrink-0" />
        
        <div className="flex-1">
          <h3 className="text-xl font-bold text-yellow-900 mb-2">
            ⚡ Actions Rapides MODE TEST
          </h3>
          
          <p className="text-sm text-yellow-800 mb-4">
            Forcez les transitions de statuts sans contrainte temporelle. Utilisez ces boutons pour simuler rapidement tout le cycle du workflow.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={() => executeAction(action)}
                disabled={loading}
                className={`${colorClasses[action.color]} text-white px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg`}
                title={action.description}
              >
                <div className="text-sm font-bold">{action.label}</div>
                <div className="text-xs mt-1 opacity-90">{action.description}</div>
              </button>
            ))}
          </div>

          {message && (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              message.includes('❌') 
                ? 'bg-red-100 border border-red-300 text-red-800' 
                : 'bg-green-100 border border-green-300 text-green-800'
            }`}>
              {message.includes('❌') ? (
                <AlertCircle className="h-5 w-5" />
              ) : (
                <span className="text-lg">✅</span>
              )}
              <span className="font-medium">{message}</span>
            </div>
          )}

          <div className="mt-4 bg-yellow-100 border border-yellow-300 p-3 rounded text-xs text-yellow-900">
            <strong>💡 Astuce :</strong> Ces boutons agissent sur TOUTES les réservations du statut source. 
            Pour tester sur une réservation spécifique, utilisez les actions individuelles dans le tableau ci-dessous.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeTestActions;